import { memo, useEffect, useState } from "react";

import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import { SvgIconTypeMap } from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";

import {
  DEFAULT_CHANNEL,
  DEFAULT_PLATFORM,
  DEFAULT_PURPOSE,
  EMAIL_PLATFORM,
  NG,
  OTP_REFRESH_SECONDS,
  PIN_LENGTH,
  OTP_REQUEST_OPTION_UNAVAILABLE,
  VOICE_MESSAGE_PLATFORM,
  WHATSAPP_PLATFORM,
  MISMATCH_PURPOSE,
} from "@zenith/data/constants";
import { ButtonComponent, PinInputComponent } from "@zenith/components";
import {
  timerElement,
  resendOtpElement,
  iconElement,
  failureResponseMessage,
} from "@zenith/features/Authentication/utils/signup";
import { setOtpValidations } from "@zenith/features/Authentication/validations/AuthValidations";
import {
  SendOtpDTO,
  UtilityApiService,
  ApiResponseDTO,
  VerifyOtpDTO,
  MfaApiService,
} from "@zenith/services/user";
import { countryCodeToEnum, startTimer } from "@zenith/utils";
import { SmsIcon } from "@zenith/assets/icons/components";
import Info from "@zenith/assets/icons/Info";
import { maskEmail } from "@zenith/utils/Formaters/maskEmail";

export interface OTPComponentProps {
  phoneNumber: string;
  email?: string;
  emailOnly?: boolean;
  externalAccountPhoneNumber?: string;
  countryCode: string;
  onVerifySuccess?: (verificationCode?: string) => void;
  onVerifySuccessWithData?: (data: any, verificationCode?: string) => void;
  onGoBack?: () => void;
  OTPPurpose?: SendOtpDTO.purpose;
  verificationType?: "default" | "sendMoney";
  backBtnInBottom?: boolean;
  align?: "center" | "start";
  isSubmitting?: boolean;
}
const OTPComponent: React.NamedExoticComponent<OTPComponentProps> = memo(
  function OTPComponent(props: OTPComponentProps): JSX.Element {
    const {
      phoneNumber,
      externalAccountPhoneNumber,
      countryCode,
      onGoBack,
      onVerifySuccess,
      onVerifySuccessWithData,
      OTPPurpose,
      backBtnInBottom = true,
      align = "center",
      email,
      emailOnly,
      verificationType = "default",
      isSubmitting = false,
    } = props;

    const [seconds, setSeconds] = useState<number>(0);
    const [showTimer, setShowTimer] = useState<boolean>(true);
    const [formData, setFormData] = useState<{ pin: string }>({
      pin: "",
    });
    const DIDNT_RECEIVE_MESSAGE = "Didn't receive the OTP?";
    const [hasEmail, setEmailState] = useState<boolean>(false);
    const [userEmail, setUserEmail] = useState("");
    const [isVerifyingOTP, setIsVerifyingOTP] = useState<boolean>(false);

    const defaultPlatform = emailOnly ? EMAIL_PLATFORM : DEFAULT_PLATFORM;

    useEffect(() => {
      UtilityApiService.sendOtp({
        channel: DEFAULT_CHANNEL as SendOtpDTO.channel,
        countryCode: countryCodeToEnum(NG),
        phoneNumber: phoneNumber,
        ...(emailOnly ? { email } : {}),
        externalAccountPhoneNumber: externalAccountPhoneNumber,
        purpose: OTPPurpose || DEFAULT_PURPOSE,
        platform: emailOnly ? EMAIL_PLATFORM : DEFAULT_PLATFORM,
      })
        .then((response) => {
          setHideOtpButton(defaultPlatform);
          const emailFromResponse = response?.data?.email;
          if (emailFromResponse) setUserEmail(emailFromResponse);
          if (response.data?.hasEmail) {
            setEmailState(response.data.hasEmail);
          }
          if (response.success) {
            createOtpSuccessMessage(defaultPlatform, emailFromResponse);
            startTimer(OTP_REFRESH_SECONDS);
            setShowTimer(true);
            resendState(true);
          }
        })
        .catch((error) => {
          manageUnavailableOptions(error.body.internalCode, DEFAULT_PLATFORM);
          resendState(
            !(error.body.internalCode === OTP_REQUEST_OPTION_UNAVAILABLE)
          );
          if (error.body.data?.hasEmail) {
            setEmailState(error.body.data.hasEmail);
          }
          setShowTimer(false);
          setHideOtpButton(defaultPlatform);

          if (error.body.internalCode === "ZENZ-CROSS-008") {
            createOtpFailureMessage(error.body.internalCode);
          } else if (
            error.body.internalCode === OTP_REQUEST_OPTION_UNAVAILABLE
          ) {
            createOtpFailureMessage(OTP_REQUEST_OPTION_UNAVAILABLE);
          } else {
            createOtpFailureMessage(defaultPlatform);
          }
        });
    }, [emailOnly]);

    const [newPin, setNewPin] = useState<Array<number | string>>(
      new Array(PIN_LENGTH).fill("")
    );

    const [pinError, setPinError] = useState<{
      message: string;
      hasError: boolean;
    }>({ message: "", hasError: false });

    /**
     * @hook otpScreenMessage
     * Used to set the message for the sendOtp requests
     */
    const [otpScreenMessage, setOtpScreenMessage] = useState<React.ReactNode>();

    // const [showEmailContainer, setShowEmailContainer] = useState<boolean>(false);
    const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

    const [hideOtpButton, setHideOtpButton] =
      useState<SendOtpDTO.platform>(defaultPlatform);

    const onBlurPinChanged = (): void => {
      setPinError(setOtpValidations(formData.pin));
    };

    const [canResend, resendState] = useState<boolean>(true);
    const [unavailableMethod, setUnavailableMethodState] = useState(
      () => new Set<SendOtpDTO.platform>()
    );

    function onPinChanged(pinEntry: number | string, index: number): void {
      const pin = [...newPin];
      pin[index] = pinEntry;
      setNewPin(pin);
      setFormData({ ...formData, pin: pin.join("") });
    }

    /**
     * @method handleOtpButtons
     * Handles the behaviour when selecting the platform type to send the otp code to
     * @param platformType
     * Platform selected (Whats app, email or voice message)
     */
    const handleOtpButtons = (platformType: SendOtpDTO.platform) => {
      switch (platformType) {
        case EMAIL_PLATFORM: {
          handleResendOTP(platformType);
          // setShowEmailContainer(true);
          break;
        }
        case WHATSAPP_PLATFORM: {
          handleResendOTP(platformType);
          break;
        }
        case VOICE_MESSAGE_PLATFORM: {
          handleResendOTP(platformType);
          break;
        }
        case DEFAULT_PLATFORM: {
          handleResendOTP(platformType);
        }
      }
    };

    /**
     * @method maskPhoneNumber
     * Masks the middle 4 digits of the phone number for security reasons.
     * @param phoneNumber
     * Phone number to be masked
     * @returns {string}
     */
    const maskPhoneNumber = (phoneNumber: string): string => {
      return phoneNumber.replace(/(\d{6})\d{4}(\d{3})/, "$1****$2");
    };

    /**
     * @method createNewOtpSuccessMessage
     * Creates the new success message format: "If the phone number X is associated with an account, you'll receive an OTP shortly"
     * @param field
     * The masked phone number or email
     * @param platform
     * Platform type for additional context
     */
    const createNewOtpSuccessMessage = (
      field: string,
      platform: SendOtpDTO.platform
    ): React.ReactNode => {
      const isEmail = platform === SendOtpDTO.platform.EMAIL;
      const fieldType = isEmail ? "email" : "phone number";
      const platformText =
        platform === SendOtpDTO.platform.WHATSAPP
          ? " via WhatsApp"
          : platform === SendOtpDTO.platform.VOICE_MESSAGE
          ? " via phone call"
          : platform === SendOtpDTO.platform.EMAIL
          ? ""
          : "";

      return (
        <span className="ml-2">
          If the {fieldType} <strong>{field}</strong> is associated with an
          account, you'll receive an OTP shortly{platformText}
        </span>
      );
    };

    /**
     * @method createOtpSuccessMessage
     * If the request returns a success it sets the OtpScreenMessage hook with the correct message
     * @param platform
     * Platform sent on the request
     */
    const createOtpSuccessMessage = (
      platform: SendOtpDTO.platform,
      emailOverride?: string
    ) => {
      let message: React.ReactNode;
      setShowErrorMessage(false);

      const maskedPhoneNumber = maskPhoneNumber(
        externalAccountPhoneNumber || phoneNumber
      );

      const resolvedEmail = emailOverride || userEmail || email || "";
      const maskedEmail = maskEmail(resolvedEmail);

      // Use the new message format for all platforms
      switch (platform) {
        case SendOtpDTO.platform.WHATSAPP:
          message = createNewOtpSuccessMessage(maskedPhoneNumber, platform);
          break;
        case SendOtpDTO.platform.VOICE_MESSAGE:
          message = createNewOtpSuccessMessage(maskedPhoneNumber, platform);
          break;
        case SendOtpDTO.platform.EMAIL:
          message = createNewOtpSuccessMessage(maskedEmail, platform);
          break;
        default:
          message = createNewOtpSuccessMessage(maskedPhoneNumber, platform);
      }

      setOtpScreenMessage(
        <span
          className={`flex w-full items-center justify-center sm:items-${align} sm:justify-${align} text-sm font-normal text-zinc-600`}
        >
          <CheckCircleOutlineOutlinedIcon color="success" />
          {message}
        </span>
      );
    };

    /**
     * @method createOtpFailureMessage
     * If the request returns a failure it sets the OtpScreenMessage hook with the correct message
     * @param platformOrCode
     * Platform sent on the requests or the internal error code received on the response
     * @param platformName
     * Platform name to be set on the max exceed attemps (ZENZ-USER-005) error
     * ! This needs to be refactored to an if else statement after the BE implements the changes
     * ! on the errors response. Now we don't have a way to know which field the error happened
     * ! After the fixes we should pass the platform and internalCode to this method. And use
     * ! an if else statement
     * * ex: if (internalCode === "ZENZ-CROSS-001" && plaform === "EMAIL_PLATFORM") and so on
     */
    const createOtpFailureMessage = (
      platformOrCode?: SendOtpDTO.platform | string
    ) => {
      let message: React.ReactNode;
      setShowErrorMessage(true);

      const maskedPhoneNumber = maskPhoneNumber(
        externalAccountPhoneNumber || phoneNumber
      );

      const maskedEmail = maskEmail(email || userEmail || "");

      switch (platformOrCode) {
        case SendOtpDTO.platform.WHATSAPP:
          message = failureResponseMessage({
            field: maskedPhoneNumber,
            message1: "The OTP could not be sent to the WhatsApp number",
            message2: "Please try again or choose another option.",
          });
          break;
        case SendOtpDTO.platform.VOICE_MESSAGE:
          message = failureResponseMessage({
            field: maskedPhoneNumber,
            message1: "We were unable to reach you on",
            message2:
              "Please try again or choose another option for OTP delivery.",
          });
          break;
        case SendOtpDTO.platform.EMAIL:
          message = failureResponseMessage({
            field: maskedEmail,
            message1: "The OTP was not sent to the email address",
            message2: "Please try again or choose another option.",
          });
          break;
        case OTP_REQUEST_OPTION_UNAVAILABLE:
          message = failureResponseMessage({
            message1:
              "You have reached the maximum number of attempts allowed. Try another option or contact support centre.",
          });
          break;
        case "ZENZ-CROSS-008":
          message = failureResponseMessage({
            message1: "User was not found by phone number",
          });
          break;
        default:
          message = failureResponseMessage({
            field: maskedPhoneNumber,
            message1: "The OTP could not be sent to the phone number",
            message2: "Please try again or choose another option.",
          });
      }

      setOtpScreenMessage(
        <span
          // Centred: full width like the success message. Side margins here
          // squeezed the text into a narrow column on the Mobile
          // verification page.
          className={`flex ${
            align === "center" ? "w-full justify-center" : "w-fit"
          } items-start text-sm font-normal text-zinc-600`}
        >
          <CancelOutlinedIcon color="error" />
          {message}
        </span>
      );
    };

    const handleResendOTP = (platformType: SendOtpDTO.platform) => {
      setOtpScreenMessage("");
      UtilityApiService.sendOtp({
        channel: DEFAULT_CHANNEL as SendOtpDTO.channel,
        countryCode: countryCodeToEnum(countryCode),
        phoneNumber,
        ...(emailOnly ? { email } : {}),
        externalAccountPhoneNumber: externalAccountPhoneNumber,
        purpose: OTPPurpose || DEFAULT_PURPOSE,
        platform: platformType,
      })
        .then((response: ApiResponseDTO) => {
          if (response.data?.hasEmail) {
            setEmailState(response.data.hasEmail);
          }
          const emailFromResponse = response?.data?.email;
          if (emailFromResponse) setUserEmail(emailFromResponse);
          setHideOtpButton(platformType);
          if (response.success) {
            resendState(true);
            createOtpSuccessMessage(platformType, emailFromResponse);
            startTimer(OTP_REFRESH_SECONDS);
            setShowTimer(true);
          }
        })
        .catch((error) => {
          if (error.data?.hasEmail) {
            setEmailState(error.data.hasEmail);
          }
          resendState(
            !(error.body.internalCode === OTP_REQUEST_OPTION_UNAVAILABLE)
          );
          manageUnavailableOptions(error.body.internalCode, platformType);
          setHideOtpButton(platformType);

          if (error.body.internalCode === "ZENZ-CROSS-008") {
            createOtpFailureMessage(error.body.internalCode);
          } else if (
            error.body.internalCode === OTP_REQUEST_OPTION_UNAVAILABLE
          ) {
            createOtpFailureMessage(OTP_REQUEST_OPTION_UNAVAILABLE);
          } else {
            createOtpFailureMessage(platformType);
          }
        });
    };

    function manageUnavailableOptions(
      errorCode: string,
      platformType: SendOtpDTO.platform
    ) {
      if (errorCode === OTP_REQUEST_OPTION_UNAVAILABLE) {
        setUnavailableMethodState((prev) => new Set(prev).add(platformType));
      }
    }

    function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
      event.preventDefault();

      setPinError(setOtpValidations(formData.pin));
      setIsVerifyingOTP(true);

      const verifyOtp = async () => {
        try {
          let response;

          if (verificationType === "sendMoney") {
            response = await MfaApiService.verifyOtp1({
              verificationCode: formData.pin,
              channel: DEFAULT_CHANNEL as any,
              countryCode:
                countryCode === NG
                  ? VerifyOtpDTO.countryCode.NG
                  : VerifyOtpDTO.countryCode.GH,
              phoneNumber,
              ...(emailOnly ? { email } : {}),
              externalAccountPhoneNumber: externalAccountPhoneNumber,
              purpose: OTPPurpose as any,
            });
          } else {
            response = await UtilityApiService.verifyOtp({
              channel: DEFAULT_CHANNEL as VerifyOtpDTO.channel,
              countryCode:
                countryCode === NG
                  ? VerifyOtpDTO.countryCode.NG
                  : VerifyOtpDTO.countryCode.GH,
              phoneNumber,
              ...(emailOnly ? { email } : {}),
              externalAccountPhoneNumber: externalAccountPhoneNumber,
              purpose: OTPPurpose as VerifyOtpDTO.purpose,
              verificationCode: formData.pin,
            });
          }

          if (response.success) {
            if (onVerifySuccessWithData) {
              onVerifySuccessWithData(response.data, formData.pin);
            } else if (onVerifySuccess) {
              onVerifySuccess(formData.pin);
            }
          } else {
            throw new Error(response.message || "Verification failed");
          }
        } catch (error: any) {
          let message = "";

          const internalCode = error.body?.internalCode;

          switch (internalCode) {
            case "ZENZ-CROSS-004":
              message = "The OTP entered is incorrect. Please try again";
              break;
            case "ZENZ-MFA-001":
              message = "MFA verification failed. Please try again";
              break;
            default:
              message =
                error.body?.message ?? "Verification failed. Please try again";
          }

          setPinError({
            message,
            hasError: true,
          });
        } finally {
          setIsVerifyingOTP(false);
        }
      };

      verifyOtp();
    }

    useEffect(() => {
      const timer = setInterval(() => {
        const remaining = localStorage.endTime - new Date().getTime();

        if (remaining >= 0) {
          setSeconds(Math.floor(remaining / 1000));
        } else {
          setShowTimer(false);
          return () => clearInterval(timer);
        }
      }, 100);
    }, [seconds, showTimer]);

    const getHideOptionsText = () => {
      const platforms = [
        EMAIL_PLATFORM,
        WHATSAPP_PLATFORM,
        VOICE_MESSAGE_PLATFORM,
        DEFAULT_PLATFORM,
      ];

      return platforms.every(
        (platform) =>
          hideOtpButton === platform ||
          unavailableMethod.has(platform) ||
          (platform === EMAIL_PLATFORM && !hasEmail)
      );
    };

    const hideOptionsText = getHideOptionsText();

    return (
      <form onSubmit={handleSubmit} className={"flex w-full grow"}>
        <div
          className={`flex grow flex-col justify-${align} ${
            align === "center" && "md:mx-10"
          }`}
        >
          <div
            className={`flex w-full flex-col items-center justify-center sm:items-${align} sm:justify-${align}`}
          >
            {otpScreenMessage}
          </div>
          {OTPPurpose === MISMATCH_PURPOSE && (
            <div className="mx-0 mt-8 w-full rounded-lg bg-violet-gradient p-4">
              <div className="flex items-start">
                <div className="mt-1">
                  <Info fill={"#5656CA"} />
                </div>
                <div className="pl-4 text-sm">
                  The phone number does not match the one linked to your Bank
                  Verification Number. Please enter the OTP sent to the linked{" "}
                  {emailOnly ? "email address." : "phone number"}
                </div>
              </div>
            </div>
          )}

          <div
            className={`flex w-full flex-row sm:justify-${align} justify-center ${
              showErrorMessage ? "mt-12" : "mt-16"
            }`}
          >
            <PinInputComponent
              id="mobile-verification-pin"
              label="Enter OTP here"
              labelClassName={`mb-3 text-neutral-600`}
              pinLength={PIN_LENGTH}
              pin={newPin}
              type={"tel"}
              masked={true}
              pinContainerClass="gap-2 md:gap-7"
              onPinChanged={onPinChanged}
              onBlur={() => {
                onBlurPinChanged();
              }}
              hasError={pinError.hasError}
              isValidating={false}
              validationMessage={pinError.message}
            />
          </div>
          {!showTimer ? "" : timerElement(seconds, align)}

          {showTimer ? (
            ""
          ) : (
            <div className={`mt-12 flex flex-col items-center justify-center`}>
              <div className="mb-8 text-base font-normal leading-normal text-neutral-500">
                {canResend &&
                  (hideOtpButton === DEFAULT_PLATFORM
                    ? resendOtpElement(
                        DIDNT_RECEIVE_MESSAGE,
                        DEFAULT_PLATFORM,
                        handleOtpButtons
                      )
                    : hideOtpButton === WHATSAPP_PLATFORM
                    ? resendOtpElement(
                        DIDNT_RECEIVE_MESSAGE,
                        WHATSAPP_PLATFORM,
                        handleOtpButtons
                      )
                    : hideOtpButton === VOICE_MESSAGE_PLATFORM
                    ? resendOtpElement(
                        DIDNT_RECEIVE_MESSAGE,
                        VOICE_MESSAGE_PLATFORM,
                        handleOtpButtons
                      )
                    : hideOtpButton === EMAIL_PLATFORM
                    ? resendOtpElement(
                        DIDNT_RECEIVE_MESSAGE,
                        EMAIL_PLATFORM,
                        handleOtpButtons
                      )
                    : "")}
              </div>
              {!emailOnly && (
                <div
                  className={`inline-flex flex-col items-${align} justify-${align} text-xs`}
                >
                  {!hideOptionsText && (
                    <div
                      className={`mb-6 inline-flex w-full flex-row items-center justify-${align} gap-2`}
                    >
                      <div className="h-px w-[40px] border border-gray-300"></div>
                      <div
                        className={`items-center justify-${align} text-sm font-normal leading-snug text-neutral-500`}
                      >
                        or choose another verification option below
                      </div>
                      <div className="h-px w-[40px] border border-gray-300"></div>
                    </div>
                  )}

                  <div
                    className={`inline-flex w-full flex-row items-${align} justify-center gap-10`}
                  >
                    {hideOtpButton === EMAIL_PLATFORM ||
                    unavailableMethod.has(EMAIL_PLATFORM) ||
                    !hasEmail
                      ? ""
                      : iconElement(
                          "mobile-verification-email-button",
                          "email",
                          "Email",
                          EMAIL_PLATFORM,
                          EmailOutlinedIcon,
                          handleOtpButtons
                        )}
                    {hideOtpButton === WHATSAPP_PLATFORM ||
                    unavailableMethod.has(WHATSAPP_PLATFORM)
                      ? ""
                      : iconElement(
                          "mobile-verification-whatsapp-button",
                          "Whatsapp",
                          "Whatsapp",
                          WHATSAPP_PLATFORM,
                          WhatsAppIcon,
                          handleOtpButtons
                        )}
                    {hideOtpButton === VOICE_MESSAGE_PLATFORM ||
                    unavailableMethod.has(VOICE_MESSAGE_PLATFORM)
                      ? ""
                      : iconElement(
                          "mobile-verification-phonecall-button",
                          "phonecall",
                          "Phone call",
                          VOICE_MESSAGE_PLATFORM,
                          PhoneInTalkOutlinedIcon,
                          handleOtpButtons
                        )}
                    {hideOtpButton === DEFAULT_PLATFORM ||
                    unavailableMethod.has(DEFAULT_PLATFORM)
                      ? ""
                      : iconElement(
                          "mobile-verification-sms-button",
                          "SMS",
                          "SMS",
                          DEFAULT_PLATFORM,
                          SmsIcon as OverridableComponent<
                            SvgIconTypeMap<object, "svg">
                          >,
                          handleOtpButtons
                        )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div
            className={`mt-14 flex items-center justify-center sm:items-${align} sm:justify-${align} flex-col gap-6 sm:flex-row`}
          >
            {backBtnInBottom && onGoBack ? (
              <ButtonComponent
                label="Back"
                onClick={onGoBack}
                size="large"
                type="secondary"
                id="back-button"
                fullWidth
              />
            ) : (
              ""
            )}

            <ButtonComponent
              label="Continue"
              disabled={
                formData.pin.length < PIN_LENGTH ||
                isVerifyingOTP ||
                isSubmitting
              }
              isLoading={isVerifyingOTP || isSubmitting}
              size="large"
              type="primary"
              fullWidth
              id="verify-otp"
            />
          </div>
        </div>
      </form>
    );
  }
);

export default OTPComponent;
